/**
 * Long-Term Disability (LTD) skeleton
 *
 * Provides the default structure for LTD coverage with empty benefit plans.
 * Returns grid format (flat fields) for use in the PlanDesigner grid.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  BenefitAmountTypeEnum,
  BenefitDurationTypeEnum,
  DefinitionOfEarningsEnum,
  EliminationPeriodTypeEnum,
  MaximumAmountTypeEnum,
  PreExistingConditionTypeEnum,
  RfpGainfulEarningsTestType,
  RfpLimitationType,
  RfpOwnOccupationPeriodType,
  RfpRehabEnum,
} from "@platform/openapi-types";

import { RfpBenefitPlan } from "../../types/rfp.api.types";

import type { LtdBenefitGrid } from "./types";

/**
 * LTD benefit plan structure for skeleton
 */
export interface LtdBenefitPlanSkeleton {
  identifier: string;
  name: string;
  numberOfLives: number;
  type: "notApplicable";
  policyPeriod: BaseBenefitPlanPolicyPeriod;
  fundingType: "fullyInsured";
  participationRequirements: string;
  rateGuarantee?: RfpBenefitPlan["rateGuarantee"];
  rateCap: string;
  optionName: "notApplicable";
  benefitClassAvailability: never[];
  rateSchedule: never[];
  benefits: LtdBenefitGrid;
}

/**
 * LTD coverage skeleton structure
 */
export interface LtdCoverageSkeleton {
  type: "ltd";
  status: "quoted";
  notes: string;
  benefitPlans: LtdBenefitPlanSkeleton[];
}

/**
 * Creates an empty LTD benefit object in grid format
 * @returns Empty LTD benefit with all fields set to default values
 */
export const getLtdBenefitSkeleton = (): LtdBenefitGrid => ({
  objectType: "ltd",

  // Text fields
  currentCarrierName: undefined,
  benefitClassAvailability: undefined,

  // Benefit Amount - flat fields for grid
  benefitAmountType: BenefitAmountTypeEnum.percentageOfSalary,
  benefitAmountAmount: undefined,
  benefitAmountMinimum: undefined,

  // Maximum Amount - flat fields for grid
  maximumAmountType: MaximumAmountTypeEnum.dollarAmount,
  maximumAmountDollarAmount: undefined,
  maximumAmountPercent: undefined,

  // Guaranteed Issue - flat fields for grid
  guaranteedIssueType: "dollarAmount",
  guaranteedIssueAmount: undefined,

  // Own Occupation Period - flat fields for grid
  ownOccupationPeriodType: RfpOwnOccupationPeriodType.yearsOwnOccupation,
  ownOccupationPeriodAmount: undefined,

  // Definition of Disability - simple enum
  definitionOfDisability: undefined,

  // Gainful Earnings Test - Own Occupation - flat fields for grid
  gainfulEarningsTestOwnOccupationType: RfpGainfulEarningsTestType.percentage,
  gainfulEarningsTestOwnOccupationAmount: undefined,

  // Gainful Earnings Test - Any Occupation - flat fields for grid
  gainfulEarningsTestAnyOccupationType: RfpGainfulEarningsTestType.percentage,
  gainfulEarningsTestAnyOccupationAmount: undefined,

  // Elimination Period - flat fields for grid
  eliminationPeriodType: EliminationPeriodTypeEnum.days,
  eliminationPeriodDays: undefined,

  // Benefit Duration - flat fields for grid
  benefitDurationType: BenefitDurationTypeEnum.toAge65,
  benefitDurationAmount: undefined,

  // Definition of Earnings - flat fields for grid
  definitionOfEarningsType: DefinitionOfEarningsEnum.salary,
  definitionOfEarningsAverageMonths: undefined,

  // Mental Illness - flat fields for grid
  mentalIllnessType: RfpLimitationType.years,
  mentalIllnessAmount: undefined,

  // Substance Abuse - flat fields for grid
  substanceAbuseType: RfpLimitationType.years,
  substanceAbuseAmount: undefined,
  // Pre-Existing Condition - flat fields for grid
  preExistingConditionType: PreExistingConditionTypeEnum.months,
  preExistingConditionMonthsDetail: undefined,

  // Simple enum fields
  rehab: RfpRehabEnum.voluntary,
  cola: undefined,
  conversion: undefined,
  waiverOfPremium: undefined,
  employerContribution: undefined,
  otherLimitationsType: undefined,
  otherLimitationsAmount: undefined,

  // Extended (non-v5) fields — CD-1940/CD-2067
  eligibilityHours: undefined,
  zeroDayResidual: undefined,
  indexingOfPreDisabilityEarnings: undefined,
  returnToWorkIncentivePeriod: undefined,
  definitionOfPartialDisability: undefined,
  otherIncomeIntegration: undefined,
  socialSecurityIntegration: undefined,
  trialWorkDays: undefined,
  survivorBenefit: undefined,
  worksiteModification: undefined,
  dayCareBenefits: undefined,
  reasonableAccommodation: undefined,
  rehabilitativeIncentiveBenefit: undefined,
  prudentPerson: undefined,
  selfReportedLimitations: undefined,
  recoveryBenefit: undefined,
  residencyRestriction: undefined,
  progressiveIllness: undefined,
  infectiousContagiousDisease: undefined,
  specificLossAdDAndLossOfSight: undefined,
  activitiesOfDailyLiving: undefined,
  maximumCapacity: undefined,
  retirementIncomeProtection: undefined,
  recurrentDisability: undefined,
  portability: undefined,
  eap: undefined,
});

/**
 * Gets the LTD coverage skeleton (kept for backwards compatibility with skeleton registry)
 */
export const getLtdSkeleton = (): LtdCoverageSkeleton => ({
  type: "ltd" as const,
  status: "quoted" as const,
  notes: "",
  benefitPlans: [
    {
      identifier: "ltd-plan-1",
      name: "Long-Term Disability Plan",
      numberOfLives: 0,
      type: "notApplicable" as const,
      policyPeriod: BaseBenefitPlanPolicyPeriod.newBusiness,
      fundingType: "fullyInsured" as const,
      participationRequirements: "",
      rateGuarantee: undefined,
      rateCap: "",
      optionName: "notApplicable" as const,
      benefitClassAvailability: [],
      rateSchedule: [],
      benefits: getLtdBenefitSkeleton(),
    },
  ],
});
